'use client';

import { Button, Spinner } from '@common/components';
import { CustomEditor, type Editor } from '@common/components/heavy';
import { useToast } from '@common/hooks';
import { SvgMailSend } from '@common/icon';
import { cn, CustomError } from '@core/utils';
import type { SaveWelloCommentResponse } from '@generated/auth';
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { Fragment, Suspense, useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import type { COMMENT_TYPE } from '@/api';
import {
  AuthApi,
  AuthInfiniteQueryKey,
  AuthQueryKey,
  UnauthQueryOptions,
} from '@/api';
import { BottomNavPortal } from '@/app/_components/BottomNavPortal';
import { ROUTES } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useAuthStore } from '@/stores/AuthStore';

interface CommentFieldContentProps {
  initOnFocus?: boolean;
  replyTo?: string;
  contentId: number;
  commentType: COMMENT_TYPE;
  rootCommentId?: number;
  parentCommentId?: number;
  isLogin: boolean;
  onSuccess?: (response?: SaveWelloCommentResponse) => void;
  topDivider?: boolean;
  className?: string;
}

const CommentFieldContent = ({
  initOnFocus,
  replyTo,
  contentId,
  commentType,
  rootCommentId,
  parentCommentId,
  isLogin,
  onSuccess,
  topDivider = true,
  className,
}: CommentFieldContentProps) => {
  const { toast, toastError } = useToast();
  const queryClient = useQueryClient();

  const [isFocused, setIsFocused] = useState(false);

  const startText = replyTo ? `@${replyTo}` : '';

  const protectedPrefix = startText
    ? `<p class="min-h-24 typo-body3 font-400"><span types="listItem,starterKit" style="color: rgb(51, 102, 255)">${startText}</span>&nbsp;</p>`
    : null;

  const tempContentForProtectedPrefix = useRef('');
  const editorRef = useRef<Editor | null>(null);

  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);

  const handleSubmitDisabled = (editor: Editor) => {
    let text = editor.getText({
      blockSeparator: '\n',
    });

    if (startText && text.startsWith(startText)) {
      text = text.slice(startText.length + 1);
    }

    const isDisabled = !text.length;

    setIsSubmitDisabled(isDisabled);
  };

  const initContent = (editor: Editor) => {
    if (protectedPrefix) {
      tempContentForProtectedPrefix.current = protectedPrefix;

      editor.commands.setContent(protectedPrefix);
    } else {
      editor.commands.setContent('');
    }
  };

  useEffect(() => {
    if (!protectedPrefix || !editorRef.current) return;
    editorRef.current.commands.setContent(protectedPrefix);
  }, [protectedPrefix]);

  const postComment = useMutation({
    mutationFn: (contents: string) =>
      AuthApi['post:/wello/v2/auth/wello-comment']({
        body: {
          commentTypeCd: commentType,
          contentId: contentId,
          contents,
          parentWelloCommentId: parentCommentId,
          rootWelloCommentId: rootCommentId,
        },
      }),
    meta: {
      reset: [
        AuthQueryKey['get:/wello/v2/auth/wello-comment'](),
        AuthQueryKey['get:/wello/v2/auth/policy/feedback/{policyId}'](),
        AuthQueryKey[
          'get:/wello/v2/auth/contents/feedback/{bestContentsId}'
        ](),
        AuthQueryKey['get:/wello/v2/auth/wello-comment/my'](),
        AuthQueryKey['get:/wello/v2/auth/community/my'](),
        AuthQueryKey[
          'get:/wello/v2/auth/hometown-news/feedback/{hometownNewsId}'
        ](),
        AuthQueryKey[
          'get:/wello/v2/auth/community/wello-news/feedback/{communityIdIdx}'
        ](),
      ],
    },

    onSuccess: async ({ data }) => {
      toast({ type: 'success', message: '댓글 등록완료' });

      //! AuthInfiniteQueryKey는 meta.reset에서 잘 작동하지 않음
      queryClient.invalidateQueries({
        queryKey: AuthInfiniteQueryKey['get:/wello/v2/auth/wello-comment'](),
      });
      queryClient.invalidateQueries({
        queryKey:
          AuthQueryKey['get:/wello/v2/auth/policy/feedback/{policyId}'](),
      });

      const context = data?.context;

      const { current: editor } = editorRef;

      if (!editor) throw new CustomError();

      initContent(editor);

      handleSubmitDisabled(editor);

      onSuccess?.(context);

      setTimeout(() => {
        editor.commands.focus();
      }, 100);
    },
    onError: (error) => {
      toastError({ error });
    },
  });

  const router = useCustomRouter();

  return (
    <div
      className={cn(
        'w-full p-16 animate-duration-150',
        {
          'border-t border-Gray-200': topDivider,
        },
        className,
      )}
    >
      <CustomEditor.Provider
        placeholder="댓글을 입력해주세요."
        onBlur={() => setIsFocused(false)}
        onCreate={({ editor }) => {
          initContent(editor);

          if (initOnFocus) {
            editor.commands.focus();
          }

          editorRef.current = editor;

          handleSubmitDisabled(editor);
        }}
        onFocus={() => {
          if (isLogin) {
            setIsFocused(true);
          } else {
            const url = new URL(window.location.href);

            router.push(
              ROUTES.LOGIN.withSearchParams({
                searchParams: {
                  redirect: url.pathname + url.search,
                },
              }),
            );
          }
        }}
        onUpdate={({ editor }) => {
          if (protectedPrefix && startText) {
            const content = editor.getHTML();

            if (!content.includes(startText) || !content.includes('&nbsp;')) {
              return editor.commands.setContent(
                tempContentForProtectedPrefix.current || protectedPrefix,
              );
            }

            tempContentForProtectedPrefix.current = content;
          }

          handleSubmitDisabled(editor);
        }}
      >
        <form
          className={cn(
            'flex max-h-96 rounded-xsmall border border-Gray-300 bg-Gray-white transition-[border] duration-150',
            {
              'border-Gray-900': isFocused,
            },
          )}
          onSubmit={(e) => {
            e.preventDefault();

            const { current: editor } = editorRef;

            if (!editor) throw new CustomError();

            let text = editor.getText({
              blockSeparator: '\n',
            });

            if (startText) {
              text = text.replace(startText, '');
            }

            text = text.trim();

            if (!text.length) throw new CustomError();

            postComment.mutate(text);
          }}
        >
          <CustomEditor.Contents className="my-11 ml-16 flex-1 p-0 hide-scrollbar typo-body3" />
          {isSubmitDisabled ? null : (
            <Button
              className="mt-auto h-46 animate-fade"
              dimensions="H40"
              disabled={postComment.isPending}
              theme="primary-text"
              type="submit"
            >
              {postComment.isPending ? (
                <Spinner size={20} />
              ) : (
                <SvgMailSend className="size-20 text-inherit" />
              )}
            </Button>
          )}
        </form>
      </CustomEditor.Provider>
    </div>
  );
};

const ReplyCommentField = (
  props: Omit<CommentFieldContentProps, 'replyTo' | 'parentCommentId'> & {
    parentCommentId: number;
  },
) => {
  const { data: nickname } = useSuspenseQuery({
    ...UnauthQueryOptions[
      'get:/wello/v2/allows/wello-comment/{welloCommentId}'
    ]({
      path: {
        welloCommentId: props.parentCommentId,
      },
    }),
    select: (data) => data.context?.nickName,
  });

  return <CommentFieldContent {...props} replyTo={nickname} />;
};

const FixedArea = () => <div className="h-81 w-full" />;

interface CommentFieldProps
  extends Omit<CommentFieldContentProps, 'isLogin' | 'replyTo'> {
  withPortal?: boolean;
}

export const CommentField = ({
  parentCommentId,
  withPortal = true,
  ...restProps
}: CommentFieldProps) => {
  const isLogin = useAuthStore((state) => state.isLogin);

  const Container = withPortal ? BottomNavPortal : Fragment;

  return (
    <Container>
      {isLogin !== undefined ? (
        <ErrorBoundary fallback={null}>
          <Suspense fallback={<FixedArea />}>
            {parentCommentId ? (
              <ReplyCommentField
                {...restProps}
                isLogin={isLogin}
                parentCommentId={parentCommentId}
              />
            ) : (
              <CommentFieldContent {...restProps} isLogin={isLogin} />
            )}
          </Suspense>
        </ErrorBoundary>
      ) : (
        <FixedArea />
      )}
    </Container>
  );
};
